import {EmojiAutocomplete} from '@github-ui/emoji-autocomplete'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useViewerSettings} from '@github-ui/use-viewer-settings'
import {FormControl, TextInput, useConfirm} from '@primer/react'
import type React from 'react'
import {type ChangeEvent, useCallback} from 'react'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import styles from './IssueTitleInput.module.css'

type IssueTitleInputProps = {
  titleRef: React.RefObject<HTMLInputElement | null>
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  commitIssueTitleEdit: () => void
  cancelIssueTitleEdit: () => void
  isDirty: boolean
  validationError?: string
}

export function IssueTitleInput({
  titleRef,
  value,
  onChange,
  cancelIssueTitleEdit,
  isDirty,
  validationError,
}: IssueTitleInputProps) {
  const confirm = useConfirm()
  const {settings} = useViewerSettings()

  const handleCloseEdit = useCallback(async () => {
    if (isDirty) {
      // Prompt user to acknowledge they want to exit without saving
      const discardChanges = await confirm({
        title: LABELS.unsavedChangesTitle,
        content: LABELS.unsavedChangesContent,
        confirmButtonType: 'danger',
      })
      if (!discardChanges) {
        return
      }
    }
    cancelIssueTitleEdit()
  }, [cancelIssueTitleEdit, confirm, isDirty])

  return (
    <>
      <GlobalCommands commands={{'issue-viewer:close-edit-title': handleCloseEdit}} />
      <FormControl className={styles.titleInputWrapper}>
        <FormControl.Label visuallyHidden>Title input</FormControl.Label>
        <EmojiAutocomplete tone={settings.emojiTone} fullWidth>
          <TextInput
            ref={titleRef}
            onChange={onChange}
            value={value}
            placeholder={LABELS.viewTitlePlaceholder}
            data-testid={TEST_IDS.issueTitleInput}
            autoFocus
            data-react-autofocus
            aria-describedby="issue-title-validation"
            aria-invalid={Boolean(validationError)}
            className={styles.titleInputWrapper}
          />
        </EmojiAutocomplete>
        {validationError && (
          <FormControl.Validation variant="error" id="issue-title-validation">
            {validationError}
          </FormControl.Validation>
        )}
      </FormControl>
    </>
  )
}
